/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { InlineNotification, Link } from '@coursera/cds-core';

import _t from 'i18n!nls/third-party-auth';

export const NotificationType = {
  INFO: 'INFO',
  LINK_ERROR: 'LINK_ERROR',
  LINK_SUCCESS: 'LINK_SUCCESS',
} as const;

export type NotificationTypeType = (typeof NotificationType)[keyof typeof NotificationType];

type Props = {
  notificationType: null | NotificationTypeType;
  onDismiss: () => void;
  isDismissable?: boolean;
  signupLink: string;
};

const styles = {
  notification: css`
    margin: var(--cds-spacing-150) 0;
  `,
};

export const SlackAccountComponentNotification = ({
  notificationType,
  isDismissable = false,
  signupLink,
  onDismiss,
}: Props) => {
  let component;
  const onDismissFn = isDismissable && !!onDismiss ? onDismiss : undefined;

  switch (notificationType) {
    case NotificationType.INFO:
      component = (
        <InlineNotification severity="information" onDismiss={onDismissFn}>
          <FormattedMessage
            message={_t(
              "Looks like you haven't set up your Slack account yet. Create a {createLink}, then enter your Slack email address below."
            )}
            createLink={
              <Link href={signupLink} variant="standard" target="blank" rel="noopener noreferrer">
                {_t('free account')}
              </Link>
            }
          />
        </InlineNotification>
      );
      break;
    case NotificationType.LINK_ERROR:
      component = (
        <InlineNotification severity="error" onDismiss={onDismissFn} role="alert">
          {_t(
            "We can't find a Slack account associated with this email address. Try another email or create a free account."
          )}
        </InlineNotification>
      );
      break;
    case NotificationType.LINK_SUCCESS:
      component = (
        <InlineNotification severity="success" onDismiss={onDismissFn} role="alert">
          {_t('Your Slack account has been linked.')}
        </InlineNotification>
      );
      break;
    default:
      component = null;
  }
  return component ? (
    <div className="slack-account-notification" css={styles.notification}>
      {component}
    </div>
  ) : null;
};

export default SlackAccountComponentNotification;
